import type { BookingSummary } from "@apartos/contracts";

const liveStatuses = ["draft", "pending_payment", "confirmed", "checked_in"];

export interface Overbooking {
  apartmentNumber: string;
  startsOn: string;
  endsOn: string;
  entries: BookingSummary[];
}

const stayDates = (entry: BookingSummary) => [entry.checkInAt.slice(0, 10), entry.checkOutAt.slice(0, 10)] as const;

// Овербукинг: две живые брони (из CRM или полоски Booking/Airbnb) на один апартамент в одни ночи.
// Прошлые пересечения не показываются: важно только то, что ещё можно исправить (выезд сегодня или позже).
export function findOverbookings(entries: BookingSummary[], todayKey: string): Overbooking[] {
  const live = entries.filter((entry) => liveStatuses.includes(entry.status) && stayDates(entry)[1] > todayKey);
  const byApartment = new Map<string, BookingSummary[]>();
  for (const entry of live) byApartment.set(entry.apartmentNumber, [...(byApartment.get(entry.apartmentNumber) ?? []), entry]);
  const result: Overbooking[] = [];
  for (const [apartmentNumber, list] of byApartment) {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const [aStart, aEnd] = stayDates(list[i]!);
        const [bStart, bEnd] = stayDates(list[j]!);
        const startsOn = aStart > bStart ? aStart : bStart;
        const endsOn = aEnd < bEnd ? aEnd : bEnd;
        if (startsOn < endsOn) result.push({ apartmentNumber, startsOn, endsOn, entries: [list[i]!, list[j]!] });
      }
    }
  }
  return result.sort((a, b) => a.startsOn.localeCompare(b.startsOn) || a.apartmentNumber.localeCompare(b.apartmentNumber, "ru", { numeric: true }));
}

export const overbookedIds = (items: Overbooking[]) => new Set(items.flatMap((item) => item.entries.map((entry) => entry.id)));

const shortDay = (value: string) => new Date(`${value}T12:00:00Z`).toLocaleDateString("ru-RU", { day: "numeric", month: "short", timeZone: "UTC" });

export function overbookingLabel(item: Overbooking): string {
  const channel = (entry: BookingSummary) => entry.externalCalendar?.provider === "airbnb" ? "Airbnb" : "Booking";
  const names = item.entries.filter((entry) => !entry.externalCalendar).map((entry) => entry.guestName);
  const unregistered = item.entries.filter((entry) => entry.externalCalendar);
  const parts = [...names];
  if (unregistered.length === 1) parts.push(`бронь ${channel(unregistered[0]!)} (гость не оформлен)`);
  if (unregistered.length > 1) parts.push(`${unregistered.length} брони ${channel(unregistered[0]!)} (гости не оформлены)`);
  return `Apt ${item.apartmentNumber}, ${shortDay(item.startsOn)} – ${shortDay(item.endsOn)}: ${parts.join(" и ")}`;
}
